import { Component, HostListener, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

export interface PhotoLightboxDialogData {
    photos: { path: string; name?: string | null }[];
    initialIndex?: number;
}

@Component({
    selector: 'app-photo-lightbox-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, AutoTranslatePipe],
    template: `
        <div class="plx">
            <div class="plx__toolbar">
                <span class="plx__title" *ngIf="current.name">{{ current.name }}</span>
                <span class="plx__spacer"></span>
                <button
                    type="button"
                    mat-icon-button
                    (click)="zoomOut()"
                    [disabled]="scale <= minScale"
                    [matTooltip]="'Diminuir zoom' | autoTranslate"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:minus'"></mat-icon>
                </button>
                <button
                    type="button"
                    mat-icon-button
                    (click)="resetZoom()"
                    [matTooltip]="'Resetar zoom' | autoTranslate"
                >
                    <span class="plx__zoom-label">{{ zoomPercent }}%</span>
                </button>
                <button
                    type="button"
                    mat-icon-button
                    (click)="zoomIn()"
                    [disabled]="scale >= maxScale"
                    [matTooltip]="'Aumentar zoom' | autoTranslate"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                </button>
                <a
                    mat-icon-button
                    [href]="current.path"
                    target="_blank"
                    rel="noopener noreferrer"
                    [matTooltip]="'Abrir original' | autoTranslate"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:arrow-top-right-on-square'"></mat-icon>
                </a>
                <button type="button" mat-icon-button (click)="close()" [matTooltip]="'Fechar' | autoTranslate">
                    <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                </button>
            </div>

            <div
                class="plx__stage"
                (wheel)="onWheel($event)"
                (pointerdown)="onPointerDown($event)"
                (pointermove)="onPointerMove($event)"
                (pointerup)="onPointerUp($event)"
                (pointercancel)="onPointerUp($event)"
                (dblclick)="onDoubleClick($event)"
            >
                <img
                    [src]="current.path"
                    [alt]="current.name || ('Imagem' | autoTranslate)"
                    class="plx__img"
                    [style.transform]="transform"
                    draggable="false"
                />
            </div>

            <div class="plx__nav" *ngIf="data.photos.length > 1">
                <button type="button" mat-icon-button (click)="prev()" [disabled]="currentIndex === 0">
                    <mat-icon [svgIcon]="'heroicons_outline:chevron-left'"></mat-icon>
                </button>
                <span class="plx__counter">{{ currentIndex + 1 }} / {{ data.photos.length }}</span>
                <button
                    type="button"
                    mat-icon-button
                    (click)="next()"
                    [disabled]="currentIndex === data.photos.length - 1"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:chevron-right'"></mat-icon>
                </button>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
                height: 100%;
                max-height: inherit;
            }

            .plx {
                display: flex;
                flex-direction: column;
                box-sizing: border-box;
                width: min(96vw, 1100px);
                height: min(92dvh, 900px);
                max-width: 96vw;
                max-height: min(92dvh, 92vh, 900px);
                background: #0b1220;
                color: #fff;
                overflow: hidden;
            }

            .plx__toolbar {
                display: flex;
                align-items: center;
                gap: 0;
                flex-shrink: 0;
                min-height: 44px;
                padding: 4px 6px;
                padding-top: max(4px, env(safe-area-inset-top, 0px));
                background: rgba(0, 0, 0, 0.55);
            }

            .plx__title {
                max-width: 36%;
                margin-left: 4px;
                font-size: 0.75rem;
                color: rgba(255, 255, 255, 0.75);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .plx__spacer {
                flex: 1;
                min-width: 4px;
            }

            .plx__zoom-label {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-width: 40px;
                font-size: 0.7rem;
                font-variant-numeric: tabular-nums;
                color: rgba(255, 255, 255, 0.9);
            }

            .plx__toolbar button,
            .plx__toolbar a,
            .plx__nav button {
                color: #fff !important;
            }

            .plx__toolbar .mat-mdc-icon-button {
                --mdc-icon-button-state-layer-size: 40px;
                width: 40px;
                height: 40px;
                padding: 0;
            }

            .plx__stage {
                position: relative;
                flex: 1 1 auto;
                min-height: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                cursor: grab;
                touch-action: none;
                user-select: none;
                -webkit-user-select: none;
            }

            .plx__stage:active {
                cursor: grabbing;
            }

            .plx__img {
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
                object-fit: contain;
                transform-origin: center center;
                transition: transform 0.05s linear;
                pointer-events: none;
            }

            .plx__nav {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 12px;
                flex-shrink: 0;
                min-height: 48px;
                padding: 6px 8px;
                padding-bottom: max(6px, env(safe-area-inset-bottom, 0px));
                background: rgba(0, 0, 0, 0.45);
            }

            .plx__counter {
                font-size: 0.85rem;
                color: rgba(255, 255, 255, 0.85);
                font-variant-numeric: tabular-nums;
            }

            @media (max-width: 640px) {
                :host {
                    height: 100%;
                    max-height: 100%;
                }

                .plx {
                    width: 100%;
                    max-width: 100%;
                    height: 100%;
                    max-height: 100%;
                    border-radius: 0;
                }

                .plx__title {
                    display: none;
                }

                .plx__toolbar {
                    gap: 0;
                    padding-inline: 2px;
                }

                .plx__toolbar .mat-mdc-icon-button {
                    --mdc-icon-button-state-layer-size: 44px;
                    width: 44px;
                    height: 44px;
                }

                .plx__zoom-label {
                    min-width: 36px;
                    font-size: 0.65rem;
                }

                .plx__stage {
                    flex: 1 1 auto;
                    min-height: 0;
                    max-height: none;
                }
            }
        `,
    ],
})
export class PhotoLightboxDialogComponent {
    readonly minScale = 1;
    readonly maxScale = 5;
    currentIndex: number;
    scale = 1;
    offsetX = 0;
    offsetY = 0;

    private dragging = false;
    private dragPointerId: number | null = null;
    private dragStartX = 0;
    private dragStartY = 0;
    private originOffsetX = 0;
    private originOffsetY = 0;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PhotoLightboxDialogData,
        private dialogRef: MatDialogRef<PhotoLightboxDialogComponent>,
    ) {
        this.currentIndex = data.initialIndex ?? 0;
        this.dialogRef.addPanelClass('photo-lightbox-panel');
    }

    get current() {
        return this.data.photos[this.currentIndex];
    }

    get zoomPercent(): number {
        return Math.round(this.scale * 100);
    }

    get transform(): string {
        return `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`;
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.close();
            return;
        }
        if (event.key === 'ArrowLeft') {
            this.prev();
            return;
        }
        if (event.key === 'ArrowRight') {
            this.next();
            return;
        }
        if (event.key === '+' || event.key === '=') {
            this.zoomIn();
            return;
        }
        if (event.key === '-') {
            this.zoomOut();
        }
    }

    zoomIn(): void {
        this.setScale(this.scale + 0.25);
    }

    zoomOut(): void {
        this.setScale(this.scale - 0.25);
    }

    resetZoom(): void {
        this.scale = 1;
        this.offsetX = 0;
        this.offsetY = 0;
    }

    onWheel(event: WheelEvent): void {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.15 : 0.15;
        this.setScale(this.scale + delta);
    }

    onDoubleClick(event: MouseEvent): void {
        event.preventDefault();
        if (this.scale > 1) {
            this.resetZoom();
        } else {
            this.setScale(2.5);
        }
    }

    onPointerDown(event: PointerEvent): void {
        if (event.button !== 0) return;
        this.dragging = true;
        this.dragPointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.originOffsetX = this.offsetX;
        this.originOffsetY = this.offsetY;
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event: PointerEvent): void {
        if (!this.dragging || this.dragPointerId !== event.pointerId) return;
        this.offsetX = this.originOffsetX + (event.clientX - this.dragStartX);
        this.offsetY = this.originOffsetY + (event.clientY - this.dragStartY);
    }

    onPointerUp(event: PointerEvent): void {
        if (this.dragPointerId !== event.pointerId) return;
        this.dragging = false;
        this.dragPointerId = null;
    }

    prev(): void {
        if (this.currentIndex <= 0) return;
        this.currentIndex--;
        this.resetZoom();
    }

    next(): void {
        if (this.currentIndex >= this.data.photos.length - 1) return;
        this.currentIndex++;
        this.resetZoom();
    }

    close(): void {
        this.dialogRef.close();
    }

    private setScale(next: number): void {
        const clamped = Math.min(this.maxScale, Math.max(this.minScale, next));
        this.scale = Math.round(clamped * 100) / 100;
        if (this.scale <= 1) {
            this.offsetX = 0;
            this.offsetY = 0;
        }
    }
}
